import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_ENDPOINTS } from '../../../config/utilityApiConfig';
import { isLocal } from '../../../config/env';
import { getSelectedUtility } from '../../../utils/utilitySelection';
import { getUtilityWorkflowUiState, updateUtilityWorkflowUiState } from '../../../utils/utilityWorkflowUiState';

const DEFAULT_SIMULATION_END_YEAR = 2050;
const MIN_SIMULATION_END_YEAR = 2027;
const MAX_FORECAST_YEAR = 2050;

function formatRawStock(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '0';
  return numericValue.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

function MdhdEvAdoptionTrendPage() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState({
    source_file: null,
    utilities: [],
    curves: [],
    selected_utility_name: null,
    mapped_forecast_zone: null,
    mapped_curve_forecast_zones: [],
    ratio_applied: 1,
    utility_population_estimate: null,
    forecast_zone_population_estimate: null,
    forecast_zone_population_count: 0,
  });
  const storedUiState = getUtilityWorkflowUiState().mdhdAdoptionTrend;
  const [selectedUtility, setSelectedUtility] = useState(storedUiState?.selectedUtility || '');
  const [simulationEndYear, setSimulationEndYear] = useState(() => {
    const nextValue = Number(storedUiState?.simulationEndYear ?? DEFAULT_SIMULATION_END_YEAR);
    if (!Number.isFinite(nextValue)) return DEFAULT_SIMULATION_END_YEAR;
    return Math.max(MIN_SIMULATION_END_YEAR, Math.min(MAX_FORECAST_YEAR, Math.round(nextValue)));
  });

  const selection = getSelectedUtility();

  const fetchCurves = useCallback(async (utilityName) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      setError('Missing auth token. Please log in again.');
      return;
    }

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (utilityName) params.utility_name = utilityName;
        const res = await axios.get(API_ENDPOINTS.getMdhdForecastCurves, {
          headers: { Authorization: `Token ${token}` },
          params,
        });
        const nextPayload = {
          source_file: res.data?.source_file ?? null,
          utilities: Array.isArray(res.data?.utilities) ? res.data.utilities : [],
          curves: Array.isArray(res.data?.curves) ? res.data.curves : [],
          selected_utility_name: res.data?.selected_utility_name ?? null,
          mapped_forecast_zone: res.data?.mapped_forecast_zone ?? null,
          mapped_curve_forecast_zones: Array.isArray(res.data?.mapped_curve_forecast_zones) ? res.data.mapped_curve_forecast_zones : [],
          ratio_applied: Number(res.data?.ratio_applied ?? 1),
          utility_population_estimate: res.data?.utility_population_estimate != null ? Number(res.data.utility_population_estimate) : null,
          forecast_zone_population_estimate: res.data?.forecast_zone_population_estimate != null ? Number(res.data.forecast_zone_population_estimate) : null,
          forecast_zone_population_count: Number(res.data?.forecast_zone_population_count ?? 0),
        };
        setPayload(nextPayload);

        if (!utilityName) {
          const preferredUtility = storedUiState?.selectedUtility || selection?.utilityName || '';
          if (preferredUtility && nextPayload.utilities.includes(preferredUtility)) {
            setSelectedUtility(preferredUtility);
          } else if (nextPayload.utilities.length > 0) {
            setSelectedUtility(nextPayload.utilities[0]);
          } else {
            setSelectedUtility('');
          }
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load MDHD adoption forecast.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [selection?.utilityName]);

  useEffect(() => {
    fetchCurves(selectedUtility);
  }, [fetchCurves, selectedUtility]);

  useEffect(() => {
    updateUtilityWorkflowUiState('mdhdAdoptionTrend', {
      selectedUtility,
      simulationEndYear,
    });
  }, [selectedUtility, simulationEndYear]);

  const visibleCurves = useMemo(() => {
    return payload.curves
      .map((curve) => ({
        ...curve,
        points: (curve.points || [])
          .filter((pt) => Number(pt.year) <= MAX_FORECAST_YEAR)
          .filter((pt) => Number(pt.year) <= simulationEndYear)
          .map((pt) => ({
            ...pt,
            vehicles: Number(pt?.vehicles),
          })),
      }))
      .filter((curve) => curve.points.length > 0);
  }, [payload.curves, simulationEndYear]);

  const visibleCurveLabel = useMemo(() => {
    if (payload.selected_utility_name) return `${payload.selected_utility_name} class 7-8 stock`;
    return 'Class 7-8 truck stock';
  }, [payload.selected_utility_name]);

  const chartData = useMemo(() => {
    const yearMap = new Map();
    visibleCurves.forEach((curve) => {
      (curve.points || []).forEach((pt) => {
        const y = Number(pt.year);
        const v = Number(pt.vehicles);
        if (!Number.isFinite(y) || !Number.isFinite(v)) return;
        if (!yearMap.has(y)) yearMap.set(y, { year: y });
        const row = yearMap.get(y);
        row.stock = (row.stock || 0) + v;
      });
    });
    return Array.from(yearMap.values())
      .map((row) => ({
        ...row,
        stock: Math.round(Number(row.stock || 0)),
      }))
      .sort((a, b) => a.year - b.year);
  }, [visibleCurves]);

  const rawStockRows = useMemo(() => {
    const yearMap = new Map();
    visibleCurves.forEach((curve) => {
      (curve.points || []).forEach((pt) => {
        const y = Number(pt.year);
        const v = Number(pt.vehicles);
        if (!Number.isFinite(y) || !Number.isFinite(v)) return;
        yearMap.set(y, (yearMap.get(y) || 0) + v);
      });
    });
    return Array.from(yearMap.entries())
      .map(([year, stock]) => ({ year, stock }))
      .sort((a, b) => a.year - b.year);
  }, [visibleCurves]);
  const xAxisTicks = useMemo(() => {
    const years = chartData.map((row) => Number(row.year)).filter(Number.isFinite);
    if (years.length <= 12) return years;
    const firstYear = years[0];
    const lastYear = years[years.length - 1];
    const ticks = years.filter((year, index) => index === 0 || index === years.length - 1 || (year - firstYear) % 2 === 0);
    if (!ticks.includes(lastYear)) ticks.push(lastYear);
    return ticks;
  }, [chartData]);

  return (
    <div className="flex min-h-screen bg-gray-50 font-nunito">
      <main className={`flex-1 bg-gray-50 overflow-y-auto ${collapsed ? 'ml-[80px]' : 'ml-[270px]'}`}>
        <div className="p-8 max-w-6xl mx-auto">
          <div className="mb-6">
            <Link to="/utility/workflow" className="text-blue-600 hover:text-blue-800 font-medium text-sm">
              ← Back to Workflow
            </Link>
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-2">MDHD EV Adoption Trend</h1>
          <p className="text-gray-600 mb-6">
            Vehicle stock forecast curves from the IEPR 2025 `Truck Stock` sheet, filtered to class 7-8 trucks through {simulationEndYear}.
          </p>

          <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            {/* <div className="grid grid-cols-1 gap-4">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-700">Utility</span>
                <select
                  value={selectedUtility}
                  onChange={(e) => setSelectedUtility(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {payload.utilities.length === 0 && <option value="">No utilities available</option>}
                  {payload.utilities.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </label>
            </div> */}
            {isLocal && (
              <div className="mt-3 text-xs text-gray-600 space-y-1">
                <label className="flex max-w-xs flex-col gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-gray-700">
                  <span className="font-medium text-amber-900">Dev-only simulation end year</span>
                  <input
                    type="number"
                    min={MIN_SIMULATION_END_YEAR}
                    max={MAX_FORECAST_YEAR}
                    step={1}
                    value={simulationEndYear}
                    onChange={(e) => {
                      const nextValue = Number(e.target.value);
                      if (!Number.isFinite(nextValue)) return;
                      setSimulationEndYear(
                        Math.max(MIN_SIMULATION_END_YEAR, Math.min(MAX_FORECAST_YEAR, Math.round(nextValue)))
                      );
                    }}
                    className="rounded border border-amber-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                  />
                  <span className="text-[11px] text-amber-800">
                    Limits the MDHD forecast years sent into the simulation for local debugging.
                  </span>
                </label>
                {payload.selected_utility_name && (
                  <p>
                    Utility selected: <span className="font-medium text-gray-800">{payload.selected_utility_name}</span>
                  </p>
                )}
                {payload.mapped_forecast_zone?.fz_name && (
                  <p>
                    Primary intersecting forecast zone:{" "}
                    <span className="font-medium text-gray-800">
                      {payload.mapped_forecast_zone.fz_name}
                    </span>
                    {payload.mapped_forecast_zone?.planning_area
                      ? ` (planning area: ${payload.mapped_forecast_zone.planning_area})`
                      : ''}
                  </p>
                )}
                {payload.mapped_curve_forecast_zones.length > 0 && (
                  <p>
                    Forecast zones used in curve:{" "}
                    <span className="font-medium text-gray-800">
                      {payload.mapped_curve_forecast_zones.map((zone) => `#${zone}`).join(', ')}
                    </span>
                  </p>
                )}
                {payload.mapped_forecast_zone?.matched_zone_count > 0 && (
                  <p>
                    Intersecting forecast zones for utility:{" "}
                    <span className="font-medium text-gray-800">{payload.mapped_forecast_zone.matched_zone_count}</span>
                    {payload.mapped_forecast_zone?.matched_zones?.length > 0
                      ? ` (${payload.mapped_forecast_zone.matched_zones
                          .map((zone) => zone?.fz_number != null ? `#${zone.fz_number}` : zone?.fz_name)
                          .filter(Boolean)
                          .join(', ')})`
                      : ''}
                  </p>
                )}
                {payload.utility_population_estimate != null && (
                  <p>
                    Utility population estimate:{" "}
                    <span className="font-medium text-gray-800">
                      {Math.round(payload.utility_population_estimate).toLocaleString()}
                    </span>
                  </p>
                )}
                {payload.forecast_zone_population_estimate != null && (
                  <p>
                    Forecast-zone population used in ratio:{" "}
                    <span className="font-medium text-gray-800">
                      {Math.round(payload.forecast_zone_population_estimate).toLocaleString()}
                    </span>
                    {payload.forecast_zone_population_count > 0 ? ` across ${payload.forecast_zone_population_count} zone(s)` : ''}
                  </p>
                )}
                <p>
                  Ratio applied to vehicle stock:{" "}
                  <span className="font-medium text-gray-800">{Number(payload.ratio_applied || 1).toFixed(3)}</span>
                </p>
              </div>
            )}
          </section>

          <section className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Electric MDHD vehicle stock forecast</h2>
            {loading ? (
              <p className="text-sm text-gray-600">Loading forecast curves...</p>
            ) : error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : chartData.length === 0 ? (
              <p className="text-sm text-gray-600">
                No MDHD curve data found. Place the IEPR 2025 workbook in `backend/utility_module/data` and ensure the `Truck Stock`
                sheet contains `Truck Class`, `Forecast Zone`, `Year`, `Stock`, and `Scenario`.
              </p>
            ) : (
              <div className="space-y-4">
                <div style={{ width: '100%', height: 380 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" ticks={xAxisTicks} interval={0} />
                      <YAxis tickFormatter={formatRawStock} />
                      <Tooltip formatter={(v) => formatRawStock(v)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="stock"
                        name={visibleCurveLabel}
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {isLocal && (
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Raw vehicle stock by year</div>
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Year</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Raw stock</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {rawStockRows.map((row) => (
                            <tr key={`raw-stock-${row.year}`}>
                              <td className="px-3 py-2 text-gray-800">{row.year}</td>
                              <td className="px-3 py-2 text-right text-gray-800">{formatRawStock(row.stock)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

export default MdhdEvAdoptionTrendPage;
